import { Service } from '@toeverything/infra';
import { type Subscription } from 'rxjs';

import type { FullscreenService } from './fullscreen.service';
import type { PresentationStateMachine } from './presentation-state-machine';
import type { WorkspaceRealtimeService } from './workspace-realtime.service';

/**
 * FollowerInputService (sl-ck8v: Socket-based)
 *
 * Single Responsibility: Block user input while following a presenter
 * - Locks viewport when following starts (in free mode)
 * - Locks viewport when in frames mode AND fullscreen (joined)
 * - UNLOCKS viewport when in frames mode but NOT fullscreen (user can explore)
 * - Handles ESC key to stop following
 *
 * Depends on:
 * - PresentationStateMachine (Single Source of Truth for state)
 * - WorkspaceRealtimeService (Server-pushed presenter updates)
 */
export class FollowerInputService extends Service {
  private subscriptions: Subscription[] = [];
  private keydownHandler?: (e: KeyboardEvent) => void;
  private syncingPresenterFullscreenExit = false;

  // Viewport controller will be set when editor binds
  private viewportController?: {
    setLocked: (locked: boolean) => void;
  };

  constructor(
    private readonly stateMachine: PresentationStateMachine,
    private readonly fullscreenService: FullscreenService,
    private readonly socketService: WorkspaceRealtimeService
  ) {
    super();
    this.setupSubscriptions();
  }

  /**
   * Bind viewport controller from editor
   * Called when editor container is bound
   */
  bindViewportController(controller: {
    setLocked: (locked: boolean) => void;
  }): () => void {
    this.viewportController = controller;

    // Apply current state
    this.applyLockState();

    return () => {
      this.setViewportLocked(false);
      this.viewportController = undefined;
    };
  }

  /**
   * Bind keyboard handler to document
   * Called when editor container is bound
   */
  bindKeyboardHandler(): () => void {
    this.keydownHandler = (e: KeyboardEvent) => {
      const state = this.stateMachine.state$.value;
      const isFollowing =
        state.type === 'following_free' || state.type === 'following_frames';

      if (e.key === 'Escape' && isFollowing) {
        e.preventDefault();
        e.stopPropagation();
        this.stateMachine.stopFollowing().catch(console.error);
      }
    };

    // Capture phase to intercept before other handlers
    document.addEventListener('keydown', this.keydownHandler, true);

    return () => {
      if (this.keydownHandler) {
        document.removeEventListener('keydown', this.keydownHandler, true);
        this.keydownHandler = undefined;
      }
    };
  }

  private setupSubscriptions(): void {
    // React to ALL relevant changes that affect lock state

    // 1. State machine changes (following starts/stops, mode changes)
    let previousState = this.stateMachine.state$.value;
    this.subscriptions.push(
      this.stateMachine.state$.subscribe(state => {
        const leftFollowerFrameMode =
          previousState.type === 'following_frames' &&
          state.type !== 'following_frames';
        previousState = state;

        if (
          leftFollowerFrameMode &&
          this.fullscreenService.isFullscreen$.value
        ) {
          void this.fullscreenService.exitFullscreen();
        }
        this.applyLockState();
      })
    );

    // 2. Fullscreen changes (affects frame mode lock)
    // IMPORTANT: Track previous state to detect fullscreen EXIT
    let wasFullscreen = this.fullscreenService.isFullscreen$.value;
    this.subscriptions.push(
      this.fullscreenService.isFullscreen$.subscribe(isFullscreen => {
        const state = this.stateMachine.state$.value;

        // If fullscreen was exited while in following_frames → exit session completely
        // This handles the case where user presses ESC in fullscreen:
        // Browser exits fullscreen (consumes event), our keydown handler doesn't get it
        if (
          wasFullscreen &&
          !isFullscreen &&
          state.type === 'following_frames' &&
          !this.syncingPresenterFullscreenExit
        ) {
          console.log(
            '[FollowerInputService] Fullscreen exited while following frames → stopping session'
          );
          this.stateMachine.stopFollowing().catch(console.error);
        }

        wasFullscreen = isFullscreen;
        this.applyLockState();
      })
    );

    // 3. Presenter list changes (mode updates)
    // sl-ck8v: Using socket service instead of presenter detection
    this.subscriptions.push(
      this.socketService.availablePresenters$.subscribe(() => {
        this.applyLockState();
      })
    );

    // 4. React to presenter mode changes via session state
    // sl-ck8v: Mode changes are handled by StateMachine via socket subscriptions
    // The StateMachine.setupSocketSubscriptions handles mode transitions
    this.subscriptions.push(
      this.socketService.sessionState$.subscribe(session => {
        if (session) {
          const state = this.stateMachine.state$.value;
          if (
            state.type === 'following_frames' &&
            !session.isFullscreen &&
            (state.isJoined || this.fullscreenService.isFullscreen$.value)
          ) {
            this.stateMachine.leaveFullscreenView();
            if (this.fullscreenService.isFullscreen$.value) {
              this.syncingPresenterFullscreenExit = true;
              void this.fullscreenService.exitFullscreen().finally(() => {
                this.syncingPresenterFullscreenExit = false;
              });
            }
          }
          // Apply lock state when session state changes
          this.applyLockState();
        }
      })
    );
  }

  /**
   * Apply lock state based on current state machine state
   *
   * SINGLE SOURCE OF TRUTH for viewport.locked
   * Only this service controls viewport.locked based on PresentationStateMachine state
   */
  private applyLockState(): void {
    const state = this.stateMachine.state$.value;
    const isFullscreen = this.fullscreenService.isFullscreen$.value;

    // Skip if controller not bound yet
    if (!this.viewportController) {
      return;
    }

    switch (state.type) {
      case 'idle':
      case 'moderating_free':
      case 'moderating_frames':
        // Not following = always unlocked
        this.setViewportLocked(false);
        break;

      case 'following_free': {
        // Free mode: lock only if presenter is ALSO in free mode (viewport sync active)
        // sl-ck8v: Use socket service's available presenters
        const presenters = this.socketService.availablePresenters$.value;
        const session = this.socketService.sessionState$.value;

        // If we have session state, use it directly
        if (session) {
          this.setViewportLocked(session.mode === 'free');
          break;
        }

        // Fallback: check presenters list
        // Note: With socket-based system, we should always have session state when following
        const presenter = presenters.length > 0 ? presenters[0] : null;
        if (!presenter) {
          this.setViewportLocked(false);
          break;
        }

        // Presenter in free mode = viewport sync active = lock
        this.setViewportLocked(presenter.mode === 'free');
        break;
      }

      case 'following_frames':
        // Frames mode: lock only if user joined (is in fullscreen)
        // If not joined (isJoined: false), user sees fullscreen prompt overlay
        this.setViewportLocked(state.isJoined && isFullscreen);
        break;
    }
  }

  /**
   * Set viewport locked state
   * This is the ONLY place that should set viewport.locked
   */
  private setViewportLocked(locked: boolean): void {
    this.viewportController?.setLocked(locked);
  }

  override dispose(): void {
    this.setViewportLocked(false);
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.subscriptions = [];
    if (this.keydownHandler) {
      document.removeEventListener('keydown', this.keydownHandler, true);
    }
    super.dispose();
  }
}
